import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, LinearProgress, Chip, Button,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fetchData, postData } from "../BaseUrl";
import Toast from "../DynamicComponent/Toast";
import AcademicCountdown from "../DynamicComponent/AcademicCountdown";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const defaultBarData = [72, 85, 90, 78, 95, 88, 70, 82, 76, 91, 68, 84];

function CalendarWidget() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthName = MONTHS[month];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Box>
      <Typography sx={{ fontWeight: "bold", mb: 1, fontSize: "14px" }}>
        {monthName} {year}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "2px" }}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <Box key={d} sx={{ textAlign: "center", fontSize: "11px", color: "#8F95B2", fontWeight: "bold", py: "2px" }}>
            {d}
          </Box>
        ))}
        {cells.map((day, idx) => (
          <Box
            key={idx}
            sx={{
              textAlign: "center",
              fontSize: "12px",
              py: "4px",
              borderRadius: "50%",
              backgroundColor: day === today.getDate() ? "linear-gradient(90deg,#4A7BA8,#244C77)" : "transparent",
              background: day === today.getDate() ? "linear-gradient(90deg,#4A7BA8,#244C77)" : "transparent",
              color: day === today.getDate() ? "#fff" : day ? "#333" : "transparent",
              fontWeight: day === today.getDate() ? "bold" : "normal",
              cursor: day ? "pointer" : "default",
            }}
          >
            {day || ""}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function TeacherDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [barData, setBarData] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [checkInStatus, setCheckInStatus] = useState({
    checkedIn: false,
    checkedOut: false,
    checkInTime: null,
    checkOutTime: null,
    consecutiveMissedDays: 0,
  });

  const loadDashboard = useCallback(async () => {
    let dash = null;
    try {
      const response = await fetchData("teacher/dashboard");
      if (response?.data) {
        dash = response.data;
        setDashboardData(response.data);
      }
    } catch (_) {}

    try {
      const attRes = await fetchData("teacher/attendance/summary");
      if (attRes?.data?.monthly) setBarData(attRes.data.monthly);
      else setBarData(dash?.attendanceByMonth || defaultBarData);
    } catch {
      setBarData(dash?.attendanceByMonth || defaultBarData);
    }
  }, []);

  const loadTodayStatus = useCallback(async () => {
    try {
      const res = await fetchData("teacher/attendance/today-status");
      if (res?.data) setCheckInStatus((prev) => ({ ...prev, ...res.data }));
    } catch (_) {}
  }, []);

  const handleCheckIn = useCallback(async () => {
    try {
      const res = await postData("teacher/attendance/check-in", {});
      const time =
        res?.data?.checkInTime ||
        new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      setCheckInStatus((prev) => ({ ...prev, checkedIn: true, checkInTime: time }));
      setToast({ open: true, severity: "success", message: "Check-in successful!" });
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Check-in failed. Please try again." });
    }
  }, []);

  const handleCheckOut = useCallback(async () => {
    try {
      const res = await postData("teacher/attendance/check-out", {});
      const time =
        res?.data?.checkOutTime ||
        new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      setCheckInStatus((prev) => ({ ...prev, checkedOut: true, checkOutTime: time }));
      setToast({ open: true, severity: "success", message: "Check-out successful!" });
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Check-out failed. Please try again." });
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadTodayStatus();
  }, [loadDashboard, loadTodayStatus]);

  const teacherName = sessionStorage.getItem("userFullName") || "Princy";
  const resolvedBarData = barData.length > 0 ? barData : defaultBarData;
  const maxBar = Math.max(...resolvedBarData);
  const assignments = dashboardData?.assignments || [];
  const schedule = dashboardData?.schedule || [];

  const kpiCards = [
    { label: "Task Submitted", value: dashboardData?.taskSubmitted || 0, icon: <AssignmentIcon sx={{ color: "#4A7BA8" }} />, bg: "#f3f0ff" },
    { label: "Exams List", value: dashboardData?.examsList || 0, icon: <SchoolIcon sx={{ color: "#244C77" }} />, bg: "#ede9fd" },
    { label: "Lessons", value: dashboardData?.lessons || 0, icon: <MenuBookIcon sx={{ color: "#4A7BA8" }} />, bg: "#f3f0ff" },
  ];

  return (
    <Box sx={{ padding: "1rem", textAlign: "left", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <AcademicCountdown />

      {/* ── Gap #13: Consecutive absence warning banner ── */}
      {checkInStatus.consecutiveMissedDays >= 2 && (
        <Box sx={{
          mb: 2, p: 2,
          backgroundColor: "#fff3e0",
          border: "1px solid #ffb74d",
          borderRadius: "12px",
          display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
        }}>
          <WarningAmberIcon sx={{ color: "#f57c00", fontSize: "28px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: "bold", color: "#e65100", fontSize: "15px" }}>
              Attendance Alert — {checkInStatus.consecutiveMissedDays} Consecutive Days Missed
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#795548" }}>
              You have missed {checkInStatus.consecutiveMissedDays} consecutive school days. A mandatory leave form may be required.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => { window.location.href = "/dashboard/teacher-leave"; }}
            sx={{
              background: "linear-gradient(135deg, #f57c00, #e65100)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: "bold",
              flexShrink: 0,
              "&:hover": { background: "linear-gradient(135deg, #e65100, #bf360c)" },
            }}
          >
            Fill Mandatory Form
          </Button>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e" }}>
          Hi {teacherName}, Welcome to{" "}
          <span style={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Cummings!
          </span>
        </Typography>
        <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: "13px" }}>
          Here's what's happening today.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        {kpiCards.map((card, i) => (
          <Grid item xs={12} sm={3} key={i}>
            <Paper sx={{ padding: "20px", borderRadius: "14px", display: "flex", alignItems: "center", gap: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", height: "100%" }}>
              <Box sx={{ backgroundColor: card.bg, borderRadius: "12px", p: "12px", display: "flex" }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" }}>{card.value}</Typography>
                <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>{card.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* ── Gap #12: Check-In / Check-Out card ── */}
        <Grid item xs={12} sm={3}>
          <Paper sx={{ padding: "16px", borderRadius: "14px", display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 1.5, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", height: "100%" }}>
            {/* Check-In */}
            <Button
              variant="contained"
              size="large"
              startIcon={<CheckCircleIcon />}
              onClick={handleCheckIn}
              disabled={checkInStatus.checkedIn}
              sx={{
                background: checkInStatus.checkedIn
                  ? "linear-gradient(135deg, #a5d6a7, #81c784)"
                  : "linear-gradient(135deg, #4caf50, #2e7d32)",
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                boxShadow: "0 3px 10px rgba(76,175,80,0.3)",
                "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #43a047, #1b5e20)" },
                "&.Mui-disabled": {
                  background: "linear-gradient(135deg, #a5d6a7, #81c784)",
                  color: "#fff",
                },
              }}
            >
              {checkInStatus.checkedIn
                ? `Checked In${checkInStatus.checkInTime ? " · " + checkInStatus.checkInTime : ""}`
                : "Check In"}
            </Button>

            {/* Check-Out */}
            <Button
              variant="contained"
              size="large"
              startIcon={<LogoutIcon />}
              onClick={handleCheckOut}
              disabled={!checkInStatus.checkedIn || checkInStatus.checkedOut}
              sx={{
                background: checkInStatus.checkedOut
                  ? "linear-gradient(135deg, #ef9a9a, #e57373)"
                  : "linear-gradient(135deg, #f44336, #c62828)",
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                boxShadow: "0 3px 10px rgba(244,67,54,0.3)",
                "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #e53935, #b71c1c)" },
                "&.Mui-disabled": {
                  background: "linear-gradient(135deg, #bdbdbd, #9e9e9e)",
                  color: "#ffffffcc",
                  opacity: 0.8,
                },
              }}
            >
              {checkInStatus.checkedOut
                ? `Checked Out${checkInStatus.checkOutTime ? " · " + checkInStatus.checkOutTime : ""}`
                : "Check Out"}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: "20px", borderRadius: "14px", background: "linear-gradient(135deg,#4A7BA8,#244C77)", color: "#fff", boxShadow: "0 4px 15px rgba(80,56,237,0.3)" }}>
                <Typography sx={{ fontSize: "13px", opacity: 0.85 }}>Student Activity</Typography>
                <Typography sx={{ fontSize: "42px", fontWeight: "bold", my: 1 }}>83%</Typography>
                <LinearProgress
                  variant="determinate"
                  value={83}
                  sx={{ borderRadius: "4px", backgroundColor: "rgba(255,255,255,0.3)", "& .MuiLinearProgress-bar": { backgroundColor: "#fff" } }}
                />
                <Typography sx={{ fontSize: "11px", mt: 1, opacity: 0.75 }}>Active students this month</Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: "20px", borderRadius: "14px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>Average Class Grade</Typography>
                <Typography sx={{ fontSize: "42px", fontWeight: "bold", color: "#1a1a2e" }}>87%</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Chip label="On track" size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "11px", borderRadius: "20px" }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={87}
                  sx={{ borderRadius: "4px", backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#4A7BA8,#244C77)" } }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: "20px", borderRadius: "14px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>Attendance Rate</Typography>
                    <Typography sx={{ fontSize: "42px", fontWeight: "bold", color: "#1a1a2e" }}>93%</Typography>
                  </Box>
                  <Chip label="This Year" size="small" sx={{ backgroundColor: "#f3f0ff", color: "#244C77", fontWeight: "bold", fontSize: "11px", borderRadius: "20px" }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "80px" }}>
                  {resolvedBarData.map((val, i) => (
                    <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: `${maxBar > 0 ? (val / maxBar) * 70 : 0}px`,
                          borderRadius: "4px 4px 0 0",
                          background: i % 2 === 0 ? "linear-gradient(180deg,#4A7BA8,#244C77)" : "#e8e4fd",
                          transition: "height 0.3s",
                        }}
                      />
                      <Typography sx={{ fontSize: "10px", color: "#8F95B2" }}>{MONTHS[i]}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Paper sx={{ p: "20px", borderRadius: "14px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                <CalendarWidget />
              </Paper>
            </Grid>

            <Grid item>
              <Paper sx={{ p: "20px", borderRadius: "14px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>Upcoming Assignments</Typography>
                {assignments.map((a, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: a.color, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#1a1a2e" }}>{a.title}</Typography>
                      <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>Due: {a.due}</Typography>
                    </Box>
                  </Box>
                ))}
                <Typography
                  onClick={() => { window.location.href = "/dashboard/teacher-biliteracy"; }}
                  sx={{ fontSize: "12px", color: "primary.main", cursor: "pointer", mt: 0.5, "&:hover": { textDecoration: "underline" } }}
                >
                  View all →
                </Typography>
              </Paper>
            </Grid>

            <Grid item>
              <Paper sx={{ p: "20px", borderRadius: "14px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "14px", mb: 0.5 }}>Schedule</Typography>
                <Typography sx={{ fontSize: "12px", color: "#8F95B2", mb: 1.5 }}>27 May</Typography>
                {schedule.map((s, i) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2, borderLeft: "3px solid #4A7BA8", pl: 1.5 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#1a1a2e" }}>{s.subject}</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>{s.time}</Typography>
                  </Box>
                ))}
                <Typography
                  onClick={() => { window.location.href = "/dashboard/teacher-leave"; }}
                  sx={{ fontSize: "12px", color: "primary.main", cursor: "pointer", mt: 0.5, "&:hover": { textDecoration: "underline" } }}
                >
                  View all Tasks →
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
